<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row class="mb-2">
      <v-col cols="12" lg="auto">
        <report-meals-select-companies></report-meals-select-companies>
      </v-col>
      <v-col cols="4" lg="auto">
        <shift-picker
          v-model="selectedShifts"
          multiple
          :disabled="!selectedCompanies.length"
        />
      </v-col>
      <v-col cols="8" lg="auto">
        <dropdown
          :label="$t('view')"
          v-model="selectedLayout"
          :items="layouts"
          hide-details
        ></dropdown>
      </v-col>
    </v-row>
    <v-row justify="center" v-if="loading">
      <v-progress-circular indeterminate color="primary" size="24"> </v-progress-circular>
    </v-row>
    <div v-for="(meal, i) in meals" :key="i">
      <h1 class="mb-4 black--text" :class="{ 'mt-12 ': i }">
        <small>{{ getMealsTotal(meal.meals) }}x</small>
        {{ meal.company.name }}
      </h1>

      <report-meals-employee-table
        v-if="selectedLayout === 1"
        :company="meal"
        :selected-shifts="selectedShifts"
      ></report-meals-employee-table>

      <div v-else>
        <v-row v-for="(mealItem, index) in meal.meals" :key="index">
          <v-col cols="12">
            <report-meals-order-card
              :meal-item="mealItem"
              :shift-display-config="shiftDisplayConfig"
              :selected-shifts="selectedShifts"
            ></report-meals-order-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsSelectCompanies from "@/components/report/meals/fields/ReportMealsSelectCompaniesField.vue";
import ReportMealsOrderCard from "@/components/report/meals/cards/ReportMealsOrderCard.vue"; // Import the new component
import ReportMealsEmployeeTable from "@/components/report/meals/components/MealEmployeeTable.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "ReportMealsTabMealEmployee",
  mixins: [hasPrinting, hasReportPrinting, hasExcel],
  components: {
    ReportMealsSelectCompanies,
    ReportMealsOrderCard, // Register the new component
    ReportMealsEmployeeTable,
  },
  data() {
    return {
      addMealDialog: false,
      loading: false,
      report: null,
      selectedShifts: [1, 2, 3], // po difoltu tri smene
      selectedLayout: 1,
    };
  },
  methods: {
    findUsers() {
      if (!this.selectedCompanies.length) {
        this.report = null; // Clear report if no companies selected
        return;
      }
      this.report = null;
      this.loading = true;
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };
      const companyIds = this.selectedCompanies.map((company) => company.id).join(",");
      reportService
        .getCompanyUserList(companyIds, params)
        .then((response) => {
          if (Object.keys(response.data).includes("meals")) {
            let item = {};
            item[companyIds] = response.data;
            this.report = item;
          } else {
            this.report = response.data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getMealsTotal(meals) {
      return meals.reduce((acc, meal) => acc + meal.total, 0);
    },
    excelDownload() {
      // Prepare Excel data
      const sheets = this.meals.map((meal) => {
        // Prepare headers
        const headers = [
          { text: this.$t("r_b"), value: "num" },
          { text: this.$t("meal"), value: "name" },
          ...this.shiftDisplayConfig
            .filter((cfg) => this.selectedShifts.includes(cfg.id))
            .map((cfg) => ({
              text: this.$t(cfg.nameKey),
              value: cfg.totalKey,
            })),
          { text: this.$t("total"), value: "total" },
        ];

        // Prepare items
        const items = meal.meals.map((mealItem, idx) => {
          const row = {
            num: idx + 1,
            name: mealItem.name,
            total: mealItem.total,
          };
          this.shiftDisplayConfig.forEach((cfg) => {
            if (this.selectedShifts.includes(cfg.id)) {
              row[cfg.totalKey] = mealItem[cfg.totalKey];
            }
          });
          return row;
        });

        // Add totals row
        const totals = {};
        this.shiftDisplayConfig.forEach((cfg) => {
          if (this.selectedShifts.includes(cfg.id)) {
            totals[cfg.totalKey] = items.reduce(
              (sum, item) => sum + (item[cfg.totalKey] || 0),
              0
            );
          }
        });
        totals.num = "";
        totals.name = this.$t("total");
        totals.total = items.reduce((sum, item) => sum + (item.total || 0), 0);
        items.push(totals);

        return {
          name: meal.company.name,
          headers,
          items,
          title: meal.company.name,
          subtitle: this.subtitle,
        };
      });

      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("meal_worker")].join(" ") + ".xlsx",
        sheets,
      });
    },
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("cards"), value: 0 },
        { text: this.$t("tableLayout"), value: 1 },
      ];
    },
    meals() {
      if (!this.report) return [];

      const searchTerm = this.filters.search
        ? this.translate(this.filters.search).toLowerCase().trim()
        : null;

      return Object.keys(this.report)
        .map((companyId) => {
          const companyData = this.report[companyId];
          if (!companyData || !companyData.meals) {
            return {
              id: companyId,
              meals: [],
              company: companyData?.company,
              users: companyData?.users,
            };
          }

          const processedMeals = companyData.meals
            .filter((meal) => meal.order_id)
            .map((originalMeal) => {
              const mealName = this.translate(originalMeal.name);
              const mealNameMatchesSearch = searchTerm
                ? mealName.toLowerCase().includes(searchTerm)
                : true;

              const filterAndProcessShift = (usersData) => {
                let users = Array.isArray(usersData) ? [...usersData] : [];

                if (searchTerm && !mealNameMatchesSearch) {
                  users = users.filter((user) => {
                    const userName = this.translate(user.name).toLowerCase();
                    const userLastname = this.translate(user.lastname).toLowerCase();
                    return (
                      userName.includes(searchTerm) || userLastname.includes(searchTerm)
                    );
                  });
                }

                const total = users.reduce((acc, user) => acc + (user.quantity || 0), 0);
                users.sort((a, b) =>
                  this.translate(a.name).localeCompare(this.translate(b.name))
                );
                return { users, total };
              };

              const shiftOneData = filterAndProcessShift(originalMeal.users?.["1-smena"]);
              const shiftTwoData = filterAndProcessShift(originalMeal.users?.["2-smena"]);
              const shiftThreeData = filterAndProcessShift(
                originalMeal.users?.["3-smena"]
              );
              const shiftFourData = filterAndProcessShift(
                originalMeal.users?.["4-smena"]
              );

              let displayTotalForMealItem = 0;
              if (this.selectedShifts.includes(1)) {
                displayTotalForMealItem += shiftOneData.total;
              }
              if (this.selectedShifts.includes(2)) {
                displayTotalForMealItem += shiftTwoData.total;
              }
              if (this.selectedShifts.includes(3)) {
                displayTotalForMealItem += shiftThreeData.total;
              }
              if (this.selectedShifts.includes(4)) {
                displayTotalForMealItem += shiftFourData.total;
              }

              // If searching, meal name doesn't match, and the display total (based on selected shifts) is 0, then hide.
              if (searchTerm && !mealNameMatchesSearch && displayTotalForMealItem === 0) {
                return null;
              }

              // If not searching, or meal name matches, or display total > 0, include it.
              // This also handles the case where meal name doesn't match search, but users in selected shifts do.
              return {
                ...originalMeal,
                name: mealName,
                total: displayTotalForMealItem, // This total is for display in card title, reflecting selected shifts
                shift_one: shiftOneData.users,
                total_shift_one: shiftOneData.total, // Potential total for shift 1 after search
                shift_two: shiftTwoData.users,
                total_shift_two: shiftTwoData.total, // Potential total for shift 2 after search
                shift_three: shiftThreeData.users,
                total_shift_three: shiftThreeData.total, // Potential total for shift 3 after search
                shift_four: shiftFourData.users,
                total_shift_four: shiftFourData.total, // Potential total for shift 4 after search
              };
            })
            .filter((meal) => meal !== null && meal.total > 0);

          return {
            id: companyId,
            meals: processedMeals,
            company: companyData.company,
            users: companyData.users.map((user) => {
              return {
                ...user,
                name: this.toLatin(user.name),
                lastname: this.toLatin(user.lastname),
              };
            }),
          };
        })
        .filter((mealEntry) => mealEntry.meals.length > 0);
    },
    selectedCompanies() {
      return this.$store.state.report.selectedMealCompanies;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    shiftDisplayConfig() {
      return [
        {
          id: 1,
          nameKey: "shift.one_dot", // Use translation key
          totalKey: "total_shift_one",
          usersKey: "shift_one",
        },
        {
          id: 2,
          nameKey: "shift.two_dot", // Use translation key
          totalKey: "total_shift_two",
          usersKey: "shift_two",
        },
        {
          id: 3,
          nameKey: "shift.three_dot", // Use translation key
          totalKey: "total_shift_three",
          usersKey: "shift_three",
        },
        {
          id: 4,
          nameKey: "shift.four_dot", // Use translation key
          totalKey: "total_shift_four",
          usersKey: "shift_four",
        },
      ];
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.findUsers();
      },
      deep: true,
    },
    selectedCompanies: {
      handler() {
        this.findUsers();
      },
      immediate: true,
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.excelDownload({});
      }
    },
    isPrinting() {
      if (this.isPrinting) {
        if (this.selectedLayout === 0) {
          // Cards format - keep existing HTML generation
          let appendHtml = "";

          this.meals.forEach((meal, mealIndex) => {
            const activeShifts = this.shiftDisplayConfig.filter((cfg) => {
              return this.selectedShifts.includes(cfg.id);
            });

            appendHtml += `
              <div class="company-section" style="
                ${mealIndex > 0 ? "page-break-before: always;" : ""}
                width: 100%;
                margin-bottom: 20px;
              ">
                <h1 style="width: 100%; text-align: center; margin: 20px 0 30px 0; font-size: 24px; color: rgba(0, 0, 0, 0.87);">
                  ${meal.company.name} (${this.getMealsTotal(meal.meals)}x)
                </h1>
            `;

            meal.meals.forEach((mealItem, itemIndex) => {
              appendHtml += `
                <div style="
                  border: 2px solid #e0e0e0;
                  border-radius: 8px;
                  margin-bottom: 15px;
                  padding: 15px;
                  background: #fff;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                  page-break-inside: avoid;
                ">
                  <div style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #eee;
                  ">
                    <h3 style="margin: 0; color: rgba(0, 0, 0, 0.87); font-size: 18px;">${mealItem.name}</h3>
                    <span style="
                      background: #f5f5f5;
                      padding: 5px 10px;
                      border-radius: 4px;
                      font-weight: bold;
                      font-size: 16px;
                      color: rgba(0, 0, 0, 0.87);
                    ">${mealItem.total}x</span>
                  </div>
              `;

              activeShifts.forEach((cfg) => {
                const shiftTotal = mealItem[cfg.totalKey] || 0;
                if (shiftTotal > 0 && mealItem[cfg.usersKey]) {
                  const usersList = mealItem[cfg.usersKey]
                    .map(
                      (user) =>
                        `${user.quantity}x ${this.toLatin(user.name)} ${this.toLatin(
                          user.lastname
                        )}`
                    )
                    .join(", ");

                  appendHtml += `
                    <div style="margin-bottom: 8px;">
                      <strong style="color: #555;">${this.$t(
                        cfg.nameKey
                      )}: ${shiftTotal}x</strong>
                      <div style="margin-left: 15px; font-size: 12px; color: rgba(0, 0, 0, 0.6); margin-top: 3px;">
                        ${usersList}
                      </div>
                    </div>
                  `;
                }
              });

              appendHtml += `</div>`;
            });

            appendHtml += `</div>`;
          });

          this.printDocument({
            title: [this.$t("report"), this.$t("meal_worker")].join(" "),
            beforeTableHtml: appendHtml,
            subtitle: this.subtitle,
            additionalStyles: `
              @media print {
                @page {
                  margin: 10mm;
                  size: A4;
                }
                .company-section {
                  page-break-inside: avoid;
                  break-inside: avoid;
                }
                .company-section:not(:first-child) {
                  page-break-before: always !important;
                  break-before: always !important;
                }
              }
              .company-section {
                padding: 10px;
                margin-bottom: 20px;
              }
            `,
          });
        } else {
          // Table format - use mixin
          const tables = this.meals.map((meal) => {
            const activeShifts = this.shiftDisplayConfig.filter((cfg) => {
              return this.selectedShifts.includes(cfg.id);
            });

            // Prepare headers
            const headers = [
              { text: this.$t("r_b"), value: "num" },
              { text: this.$t("meal"), value: "name" },
              ...activeShifts.map((cfg) => ({
                text: this.$t(cfg.nameKey),
                value: cfg.totalKey,
              })),
              { text: this.$t("total"), value: "total" },
            ];

            // Prepare items
            const items = meal.meals.map((mealItem, idx) => {
              const row = {
                num: idx + 1,
                name: mealItem.name,
                total: mealItem.total,
              };
              activeShifts.forEach((cfg) => {
                let users = mealItem.users[cfg.id + "-smena"] || [];
                const userOrders = users.map((u) => {
                  return (
                    `<span style="font-weight: bold;">${u.quantity}x </span>` +
                    [this.toLatin(u.name), this.toLatin(u.lastname)].join(" ")
                  );
                });
                row[cfg.totalKey] = mealItem[cfg.totalKey] ? userOrders.join(", ") : 0;
              });
              return row;
            });

            // Add totals row
            const totals = {
              num: "",
              name: this.$t("total"),
              total: this.getMealsTotal(meal.meals),
            };
            activeShifts.forEach((cfg) => {
              totals[cfg.totalKey] = items.reduce((sum, item) => {
                const value = item[cfg.totalKey];
                if (typeof value === "string" && value.includes("x ")) {
                  // Extract number before 'x' from format like "5x John Doe"
                  const match = value.match(/^(\d+)x\s/);
                  return sum + (match ? parseInt(match[1]) : 0);
                }
                return sum + (parseInt(value) || 0);
              }, 0);
            });
            items.push(totals);

            return {
              title:
                `${this.getMealsTotal(meal.meals)}x ` +
                meal.company.name +
                " " +
                this.subtitle,
              headers,
              items,
              showCaption: true,
            };
          });

          this.printDocument({
            title: [this.$t("report"), this.$t("meal_worker")].join(": "),
            tables,
            hideDatetime: true,
            additionalStyles: `
              .print-table {
                page-break-after: always;
              }
              .print-table:last-child {
                page-break-after: auto;
              }
            `,
          });
        }
      }
    },
  },
};
</script>
